import React, { useMemo } from "react";
import { StreamlineIcon } from "src/components/icons";
import ScrollToErrorForm from "src/components/scroll_to_error_form";

import { newCustomerSessionPath } from "src/routes";
import removeIconUrl from "images/icons/remove.svg";

const CreateAccountForm = ({
  errors,
  isSubmitting,
  onSubmit,
  clearErrors,
  signInSelectCallback,
  onClose,
  initialEmail,
  initialPassword,
  initialPasswordConfirmation,
  initialFirstName,
  initialLastName,
  initialPhoneNumber,
  hasStickyHeader,
}: {
  errors: Record<string, string[]>;
  isSubmitting: boolean;
  onSubmit: (formData: FormData) => void;
  clearErrors: () => void;
  signInSelectCallback?: () => void;
  onClose?: () => void;
  hasStickyHeader?: boolean;
  initialEmail?: string;
  initialPassword?: string;
  initialPasswordConfirmation?: string;
  initialFirstName?: string;
  initialLastName?: string;
  initialPhoneNumber?: string;
}) => {
  const [email, setEmail] = React.useState(initialEmail ?? "");
  const [password, setPassword] = React.useState(initialPassword ?? "");
  const [passwordConfirmation, setPasswordConfirmation] = React.useState(initialPasswordConfirmation ?? "");
  const [firstName, setFirstName] = React.useState(initialFirstName ?? "");
  const [lastName, setLastName] = React.useState(initialLastName ?? "");
  const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber ?? "");

  const headerRef = React.useRef<HTMLElement>(null);

  const submitSignUp = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(new FormData(e.currentTarget));
  }

  const onSignInSelect = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isSubmitting) {
      e.preventDefault();
      return;
    }

    if (signInSelectCallback) {
      e.preventDefault();
      signInSelectCallback();
    }
  }

  const fieldErrors = (errorKey: string) => {
    const fieldErrorMessages = errors[errorKey] || [];

    if (fieldErrorMessages.length > 0) {
      return (
        <div className="error" data-testid="fieldError">
          {fieldErrorMessages.join(". ")}
        </div>
      );
    } else {
      return null;
    }
  }

  const hasAnyErrors = useMemo(() => Object.values(errors).some((errors) => errors.length > 0), [errors])

  return (
    <>
      <section className="divider has-content has-high-contrast is-stuck-top is-layer-middle" data-stickifier-target={hasStickyHeader ? "stackable" : undefined} ref={headerRef}>
        <h3 className="space-mb-none">Sign up</h3>
        {onClose &&
          <button className="button is-icon-only has-large-icon is-ghost is-aligned-right" name="button" aria-label="Close" onClick={onClose}>
            <StreamlineIcon href={removeIconUrl} />
          </button>
        }
      </section>

      <section className="container content space-pt-none">
        <ScrollToErrorForm errors={errors} stickyHeaderRef={headerRef} onSubmit={submitSignUp}>
          <fieldset className={hasAnyErrors ? "has-error" : undefined}>
            <div className="text-input has-max-width">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                autoComplete="email"
                autoFocus={true}
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-cy="customer-email-input"
                required
              />
              {fieldErrors("email")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                autoComplete="new-password"
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-cy="customer-new-password-input"
                required
              />
              <div className="helper">
                Must be at least 8 characters
              </div>
              {fieldErrors("password")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="password_confirmation">Password confirmation</label>
              <input
                id="password_confirmation"
                type="password"
                name="password_confirmation"
                autoComplete="new-password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                data-cy="customer-confirm-password-input"
                required
              />
              {fieldErrors("password_confirmation")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="first_name">First name</label>
              <input
                id="first_name"
                type="text"
                name="first_name"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-cy="customer-first-name-input"
                required
              />
              {fieldErrors("first_name")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="last_name">Last name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={lastName}
                autoComplete="family-name"
                onChange={(e) => setLastName(e.target.value)}
                data-cy="customer-last-name-input"
                required
              />
              {fieldErrors("last_name")}
            </div>

            <div className="text-input has-max-width">
              <label htmlFor="phone_number">Phone number</label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={phoneNumber}
                autoComplete="tel"
                onChange={(e) => setPhoneNumber(e.target.value)}
                data-cy="customer-phone-number-input"
                required
              />
              {fieldErrors("phone_number")}
            </div>

            {fieldErrors("base")}
          </fieldset>

          <button
            type="submit"
            className="button is-primary"
            disabled={isSubmitting}
            onClick={clearErrors}
            data-cy="customer-submit-button">Sign up
          </button>
        </ScrollToErrorForm>

        <p className="space-mt-xs">
          Already have an account? <a className="has-underline" href={newCustomerSessionPath()} onClick={onSignInSelect}>Sign in</a>
        </p>
      </section>
    </>
  );
}

export default CreateAccountForm;
